import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit"
import { ListColorxTintedProductsRes_Result as ColorxTintedProductsResult } from "gen/api/colorx/service_pb"
import { ValidatedFanBuilderLoyaltyCoupon } from "gen/api/customer/service_pb"
import { GetSaleRes, SyncCartPricesRes } from "gen/api/sale/service_pb"
import {
  AdditionalFees_Fee as AdditionalFee,
  SaleForm_CustomField,
  Staff,
} from "gen/company/models_pb"
import {
  Customer,
  Contacts_Contact as CustomerContact,
  PurchaseOrder,
} from "gen/customer/models_pb"
import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"
import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { Product, Product_TaxStatus } from "gen/product/models_pb"
import { Address as PBAddress } from "gen/proto/address/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { TintColor } from "gen/tintcolor/models_pb"
import {
  Sale,
  SaleProduct as SaleProductPB,
  SaleProduct_Type as SaleProductType,
  Sale_Type as SaleType,
  TenderSubmissionMetadata_SubmissionType,
} from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { FractionalMoney, Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import { Address } from "shared-ts/libs/address"
import {
  convertUoMQuantityToQuantity,
  safeConvertQuantityToUoMQuantity,
} from "shared-ts/libs/product/unitsOfMeasure.helpers"
import { OptionType, OptionTypes } from "shared-ts/types/tender/tender.types"

import { AppState } from "app/store"
import {
  DisplayableTenderSubmissionMetadata,
  calculateSurchargeFromTenderSubmissions,
} from "components/composite/TenderDrawer"
import { salesApiSlice } from "features/api/sale"
import { CustomerState } from "features/pos/transactions/CustomerSidebar/CustomerSidebar"
import { isID } from "lib/isId"
import { stringify } from "lib/stringify"

import {
  convertSalesToSaleLinks,
  convertReturnedInSalesToSaleLinks,
} from "./components"
import { isFulfillmentEqual, StrictFulfillment } from "./FulfillmentLocation"
import {
  applySaleUomSelection,
  getSaleUomInfo,
  type SaleUomUsage,
} from "./helpers"
import {
  findIdxInSaleProducts,
  getExistingProductWithNotesInCartIdx,
} from "./helpers/helpers"
import {
  isLinkedReturn,
  isReturn,
  isReturnType,
  isUnlinkedReturn,
} from "./helpers/saleProduct"
import { SpecialOrderProductSaleFormData } from "./SpecialOrderPriceAndCostUpdateModal"
import SaleForm, {
  ChosenDepositValue,
  RestockFeeObjectType,
  SaleLink,
  SaleProduct,
} from "./types/SaleForm.types"
import {
  checkDiscountEquality,
  checkProductArrayEquality,
} from "../transactions/helpers/helpers"
import { TransactionProduct } from "../transactions/transactionProduct"
import { buildSaleProducts } from "./adapters/saleProduct.adapter"
import { ValueTypes } from "./SaleTotalCostFields/ValueTypeInput"

export type RestockFee =
  | { type: "rate"; rate: Decimal }
  | { type: "fixed"; amount: Money }
  | null

export function restockFeeFromSale(sale: GetSaleRes["sale"]): RestockFee {
  if (
    sale?.restockFeePercentage &&
    !Decimal.fromPB(sale.restockFeePercentage).isZero()
  ) {
    return {
      type: RestockFeeObjectType.RATE,
      rate: Decimal.fromPB(sale.restockFeePercentage).mult(
        Decimal.fromBigSource(100)
      ),
    }
  }
  if (sale?.restockFeeAmount && !Money.fromPB(sale.restockFeeAmount).isZero()) {
    return {
      type: RestockFeeObjectType.FIXED,
      amount: Money.fromPB(sale.restockFeeAmount),
    }
  }
  return null
}

export type Discount =
  | {
      type: "amount"
      amount: Money // negative value
    }
  | {
      type: "rate"
      rate: Decimal // positive value 0-1
    }
  | null

function discountFromSale(sale: GetSaleRes["sale"]): Discount {
  if (sale?.discountRate) {
    return { type: "rate", rate: Decimal.fromPB(sale.discountRate) }
  }
  if (sale?.discountAmount) {
    return { type: "amount", amount: Money.fromPB(sale.discountAmount) }
  }
  return null
}

export interface productForReturn extends uniqueProductIdentifier {
  quantityToReturn: number
}

export type uniqueProductIdentifier = {
  id?: PBUUID // sale product id
  productId?: PBUUID
}

export type uniqueTintIdentifier = {
  productId?: PBUUID
  tintColorId?: PBUUID
  customTintColor: string
}

export type SetSaleInitialPayloadT = GetSaleRes

export type ItemSetSubmissionArgs = {
  assignToItemSet: (saleProductId: UUID) => void
  itemSetId?: UUID
  itemSetIdBySaleProductId: Record<string, string>
}

export type PriceEditedSubmissionArgs =
  | {
      isPriceEdited: true
      customPrice: Money
      customRetailPrice?: Money
    }
  | {
      isPriceEdited?: false
    }

export type CostEditedSubmissionArgs =
  | {
      isCostEdited: true
      customCost: FractionalMoney
    }
  | {
      isCostEdited?: false
    }

export type SearchProductSubmittedPayload = {
  uomId?: PBUUID
  uomSnapshot?: LedgerEntry_UomSnapshot
  notes?: string
  internalNotes?: string
  tintColor?: TintColor
  customTintColor?: string
  saleProductType?: SaleProductType
  linkedTransactionProductId?: UUID
  linkedTransactionId?: UUID
  linkedTransactionIdentifier?: string
  linkedTransactionType?: SaleType
  quantity?: Decimal // This is the signed quantity of items to return, from the return modal form.
  linkedQuantityRemaining?: Decimal
  saleProductId?: PBUUID
  giftCardId?: PBUUID
  giftCardInitialBalance?: Money
  giftCardPrice?: Money
  specialOrderProductNotes?: string
  specialOrderInternalNotes?: string
  customFields?: SaleForm_CustomField[]
} & PriceEditedSubmissionArgs &
  CostEditedSubmissionArgs &
  ItemSetSubmissionArgs

type ProductPayloadAction<T> = PayloadAction<uniqueProductIdentifier & T>

export type ProductTintSelection =
  | { tintColor: TintColor; customTintColor: "" }
  | { tintColor?: undefined; customTintColor: string }

export interface LastModifiedSaleProductMarker {
  id: PBUUID
  version: number
  isNewLine?: boolean
}

export interface SaleFormState extends SaleForm {
  initialState?: SaleForm
  isEdited?: boolean
  isTaxRateFromDeliveryAddress?: boolean
  isInitializing?: boolean
  cartTotals?: SyncCartPricesRes
  chosenDepositValue?: ChosenDepositValue
  // quantityRemainig is quantitySold - quantityReturned
  quantityRemainingBySaleProductId?: Record<string, Decimal>
  restockFee?: RestockFee
  // Version counter ensures UI effect runs even when same sale product updated twice
  lastModifiedSaleProduct?: LastModifiedSaleProductMarker
  discount?: Discount
  /**
   * Fan Builder coupons on the cart. Sale state rather than panel state: they change the total, a
   * sale is edited when one is added, and they belong to the customer they were validated for.
   */
  fanBuilderCoupons?: ValidatedFanBuilderLoyaltyCoupon[]
}

const initialState: SaleFormState = {
  restockFee: null,
  fanBuilderCoupons: [],
}

function updateProductTint(
  state: SaleFormState,
  productToEditId: PBUUID | undefined,
  tint: ProductTintSelection,
  fields: Partial<SaleProduct> = {}
) {
  if (!productToEditId || !state.saleProducts) {
    return
  }

  const productIdx = findIdxInSaleProducts(state.saleProducts, productToEditId)
  if (productIdx === -1) {
    return
  }

  state.saleProducts[productIdx] = {
    ...state.saleProducts[productIdx],
    ...fields,
    tintColorId: tint.tintColor?.id,
    customTintColor: tint.tintColor ? "" : tint.customTintColor,
  }

  if (tint.tintColor?.id) {
    state.tintColorsById = {
      ...state.tintColorsById,
      [UUID.idString(tint.tintColor.id)]: tint.tintColor,
    }
  }

  state.isEdited = checkIsEdited(state)
}

const sortTaxRateIds = (ids: PBUUID[]): PBUUID[] =>
  [...ids].sort((a, b) => UUID.idString(a).localeCompare(UUID.idString(b)))

// Prefer the sale's full set of tax rates, falling back to the legacy single
// tax rate for sales saved before multi-rate support existed.
const taxRateIdsFromSale = (sale?: Sale): PBUUID[] | undefined => {
  if (sale?.taxRateIds?.length) return sortTaxRateIds(sale.taxRateIds)
  if (sale?.taxRateId) return sortTaxRateIds([sale.taxRateId]) // TODO: (multi-tax) remove
  return undefined
}

const taxRateIdsEqual = (a?: PBUUID[], b?: PBUUID[]): boolean => {
  const aIds = a ?? []
  const bIds = b ?? []
  if (aIds.length !== bIds.length) return false
  return aIds.every((id, i) => UUID.eqFromPB(id, bIds[i]))
}

// The deposit is resolved by SyncCartPrices; compare the snapshot against the
// last resolved value, falling back to the snapshot before the first response
// lands so a not-yet-synced sale isn't flagged as edited.
const depositEqual = (state: SaleFormState): boolean =>
  Money.eqFromPB(
    state.initialState?.initialDeposit,
    state.cartTotals?.initialDeposit ?? state.initialState?.initialDeposit
  )

const checkIsEdited = (state: SaleFormState) => {
  const original = state.initialState
  const current = state
  const edits: boolean[] = [
    original?.saleNotes !== current.saleNotes,
    stringify(original?.fanBuilderCoupons ?? []) !==
      stringify(current.fanBuilderCoupons ?? []),
    original?.internalSaleNotes !== current.internalSaleNotes,
    original?.quoteExpiration?.getTime() !== state.quoteExpiration?.getTime(),
    !UUID.eqFromPB(original?.location?.id, current.location?.id),
    !isFulfillmentEqual(original?.fulfillment, current.fulfillment),
    !UUID.eqFromPB(original?.purchaseOrder?.id, current.purchaseOrder?.id),
    !Address.eqFromPB(original?.address, current.address),
    state.initialState?.customerContact?.idString !==
      current.customerContact?.idString,
    !checkProductArrayEquality(
      original?.saleProducts ?? [],
      state.saleProducts ?? []
    ),
    current.searchProduct !== undefined,
    !current.addProductQuantity?.eq(Decimal.fromBigSource(1)),
    !taxRateIdsEqual(original?.taxRateIds, current.taxRateIds),
    !depositEqual(current),
    !checkDiscountEquality(original?.discount, current.discount),
    !UUID.eqFromPB(
      original?.designatedClerkId ?? undefined,
      current.designatedClerkId ?? undefined
    ),
  ]
  return edits.some((edit) => edit)
}

/* eslint-disable complexity */
export const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {
    setIsInitializing: (state) => {
      state.isInitializing = true
    },
    setSaleInitialState: (
      state,
      action: PayloadAction<SetSaleInitialPayloadT>
    ) => {
      const saleProducts = buildSaleProducts(action.payload.saleProducts)
      const initialDeposit = action.payload.sale?.initialDeposit
        ? Money.fromPB(action.payload.sale.initialDeposit)
        : undefined
      const designatedClerkId = action.payload.sale?.designatedClerkId
      const discount = discountFromSale(action.payload.sale)
      state.initialState = {
        location: action.payload.location,
        designatedClerkId,
        fulfillment: {
          location: action.payload.fulfillmentLocation ?? null,
          isDelivery: Boolean(action.payload.sale?.isDeliveryFulfillment),
          deliveryDate: action.payload.sale?.deliveryDate
            ? TimestampPB.toDate(action.payload.sale?.deliveryDate)
            : null,
        },
        purchaseOrder: action.payload.customerPurchaseOrder,
        address: action.payload.sale?.address,
        productsById: action.payload.productsById,
        tintColorsById: action.payload.tintColorsById,
        catalogProductsById: action.payload.catalogProductsById,
        saleNotes: action.payload.sale?.saleNotes,
        internalSaleNotes: action.payload.sale?.internalNotes,
        quoteExpiration: action.payload.sale?.quoteExpiresAt
          ? TimestampPB.toDate(action.payload.sale?.quoteExpiresAt)
          : undefined,
        customerId: action.payload.sale?.customerId,
        inventoriesByProductId: action.payload.inventoriesByProductId,
        splitPayments: [],
        isSplitPayment: false,
        splitAmount: undefined,
        taxRateIds: taxRateIdsFromSale(action.payload.sale),
        saleProducts: saleProducts,
        returnedFromSalesById: convertSalesToSaleLinks(
          action.payload.returnedFromSalesById
        ),
        returnedInSalesBySaleProductId: convertReturnedInSalesToSaleLinks(
          action.payload.returnedInSalesBySaleProductId
        ),
        additionalDeposit: undefined,
        initialDeposit,
        discount,
        // A loaded sale never carries coupons: they are only recorded by a sale that completes, and
        // nothing hydrates them back. Snapshotting [] keeps a scan on the loaded sale an edit.
        fanBuilderCoupons: [],
      }
      state.location = action.payload.location
      state.fulfillment = {
        location: action.payload.fulfillmentLocation ?? null,
        isDelivery: Boolean(action.payload.sale?.isDeliveryFulfillment),
        deliveryDate: action.payload.sale?.deliveryDate
          ? TimestampPB.toDate(action.payload.sale?.deliveryDate)
          : null,
      }
      state.purchaseOrder = action.payload.customerPurchaseOrder
      state.address = action.payload.sale?.address
      state.customerContact = action.payload.customerContact
      state.designatedClerkId = designatedClerkId
      state.productsById = action.payload.productsById
      state.tintColorsById = action.payload.tintColorsById
      state.catalogProductsById = action.payload.catalogProductsById

      state.saleProducts = saleProducts
      state.saleNotes = action.payload.sale?.saleNotes
      state.internalSaleNotes = action.payload.sale?.internalNotes
      state.quoteExpiration = action.payload.sale?.quoteExpiresAt
        ? TimestampPB.toDate(action.payload.sale?.quoteExpiresAt)
        : undefined
      state.customerId = action.payload.sale?.customerId
      state.cartTotals = undefined
      state.chosenDepositValue = undefined

      state.isEdited = false
      state.isInitializing = false
      state.addProductQuantity = Decimal.fromBigSource(1)
      state.inventoriesByProductId = action.payload.inventoriesByProductId
      state.splitPayments = []
      state.isSplitPayment = false
      state.tenderOption = undefined
      state.splitAmount = undefined
      state.taxRateIds = taxRateIdsFromSale(action.payload.sale)
      state.idempotencyKey = UUID.newRandom().toString()
      state.isCardSurchargeExempt = false
      state.additionalDeposit = undefined
      state.returnedFromSalesById = convertSalesToSaleLinks(
        action.payload.returnedFromSalesById
      )
      state.returnedInSalesBySaleProductId = convertReturnedInSalesToSaleLinks(
        action.payload.returnedInSalesBySaleProductId
      )
      state.quantityRemainingBySaleProductId = {}
      for (const [key, value] of Object.entries(
        action.payload.quantityRemainingBySaleProductIdV2
      )) {
        state.quantityRemainingBySaleProductId[key] = Decimal.fromPB(value)
      }
      state.restockFee = restockFeeFromSale(action.payload.sale)
      state.discount = discount
      // The store is a singleton, so without this the previous sale's coupons stay on it. The
      // customerSelected guard cannot catch that: customerId has already been replaced above, so
      // re-selecting the same customer compares equal and clears nothing.
      state.fanBuilderCoupons = []
    },
    resetSaleStateForEditableSale: (
      state,
      action: PayloadAction<GetSaleRes>
    ) => {
      const fresh = action.payload
      const saleProducts = buildSaleProducts(fresh.saleProducts)

      state.saleProducts = saleProducts
      state.additionalDeposit = undefined
      state.productsById = fresh.productsById
      state.tintColorsById = fresh.tintColorsById
      state.catalogProductsById = fresh.catalogProductsById
      state.inventoriesByProductId = fresh.inventoriesByProductId
      state.restockFee = restockFeeFromSale(fresh.sale)
      state.discount = discountFromSale(fresh.sale)
      state.taxRateIds = taxRateIdsFromSale(fresh.sale)
      state.isTaxRateFromDeliveryAddress = false

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    resetSaleDetails: (state, action: PayloadAction<GetSaleRes>) => {
      const fresh = action.payload
      const fulfillment = {
        location: fresh.fulfillmentLocation ?? null,
        isDelivery: Boolean(fresh.sale?.isDeliveryFulfillment),
        deliveryDate: fresh.sale?.deliveryDate
          ? TimestampPB.toDate(fresh.sale.deliveryDate)
          : null,
      }

      state.fulfillment = fulfillment
      state.purchaseOrder = fresh.customerPurchaseOrder
      state.address = fresh.sale?.address
      state.customerContact = fresh.customerContact
      state.saleNotes = fresh.sale?.saleNotes
      state.internalSaleNotes = fresh.sale?.internalNotes
      state.designatedClerkId = fresh.sale?.designatedClerkId

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    resetSaleForm: (state) => {
      for (const key in state) {
        delete state[key as keyof SaleFormState]
      }
      state.addProductQuantity = Decimal.fromBigSource(1)
      state.initialState = {
        addProductQuantity: Decimal.fromBigSource(1),
      }
      state.isEdited = false
      state.idempotencyKey = UUID.newRandom().toString()
    },
    incrementReturnQuantities: (
      state,
      action: PayloadAction<Record<string, Decimal>>
    ) => {
      if (!state.saleProducts) {
        return
      }

      for (const sp of state.saleProducts) {
        if (!sp.id) {
          continue
        }
        const spID = UUID.fromPB(sp.id).toString()
        if (spID in action.payload) {
          sp.quantityReturnedV2 = (
            sp.quantityReturnedV2
              ? Decimal.fromPB(sp.quantityReturnedV2)
              : Decimal.zero()
          ).add(action.payload[spID])
        }
      }
    },
    setInitialQuoteExpiration: (state, action: PayloadAction<Date>) => {
      state.initialState = {
        ...state.initialState,
        quoteExpiration: action.payload,
      }
      state.quoteExpiration = action.payload

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setQuoteExpiration: (state, action: PayloadAction<Date>) => {
      state.quoteExpiration = action.payload

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    customerSelected: (state, action: PayloadAction<CustomerState>) => {
      // The PO, contact and coupons belong to the previous customer; keep them only when
      // re-selecting the same one (e.g. the redispatch on sale load). A coupon validated for one
      // customer must never discount another's cart.
      if (!UUID.eqFromPB(state.customerId, action.payload?.customerId)) {
        delete state.purchaseOrder
        delete state.customerContact
        state.fanBuilderCoupons = []
      }
      state.customerId = action.payload?.customerId
      state.isCardSurchargeExempt =
        action.payload?.isCustomerCardSurchargeExempt
      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    fanBuilderCouponsChanged: (
      state,
      action: PayloadAction<ValidatedFanBuilderLoyaltyCoupon[]>
    ) => {
      state.fanBuilderCoupons = action.payload
      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    resetCustomer: (state) => {
      delete state.customerId
      delete state.customerContact
      delete state.purchaseOrder
      state.fanBuilderCoupons = []
      state.tenderOption = undefined

      state.isEdited = checkIsEdited(state as SaleFormState)
      state.isCardSurchargeExempt = false
    },
    removeProduct: (
      state,
      action: PayloadAction<{ saleProduct: SaleProduct }>
    ) => {
      const { saleProduct } = action.payload
      if (
        !state.saleProducts ||
        state.saleProducts.length === 0 ||
        !saleProduct.id
      ) {
        return
      }
      const productIdx = findIdxInSaleProducts(
        state.saleProducts,
        saleProduct.id
      )

      if (productIdx === -1) {
        return
      }

      state.saleProducts.splice(productIdx, 1)[0]

      // update position values for all products
      state.saleProducts = state.saleProducts.map((product, index) => ({
        ...product,
        position: index + 1,
      }))

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setAddProductQuantity: (state, action: PayloadAction<Decimal>) => {
      state.addProductQuantity = action.payload
      state.isEdited
    },
    setAdditionalDeposit: (state, action: PayloadAction<Money>) => {
      state.additionalDeposit = action.payload
    },
    setChosenDepositValue: (
      state,
      action: PayloadAction<ChosenDepositValue>
    ) => {
      state.chosenDepositValue = action.payload
    },
    setProductQuantityV2: (
      state,
      action: ProductPayloadAction<{
        quantity?: Decimal
        convertToSale?: boolean
      }>
    ) => {
      if (!action.payload.id || !state.saleProducts) {
        return
      }

      const productIdx = findIdxInSaleProducts(
        state.saleProducts,
        action.payload.id
      )

      if (productIdx === -1) {
        return
      }

      if (
        action.payload.convertToSale &&
        state.saleProducts[productIdx].type === SaleProductType.UNLINKED_RETURN
      ) {
        state.saleProducts[productIdx] = {
          ...state.saleProducts[productIdx],
          type: SaleProductType.SALE,
          quantityV2: action.payload.quantity ?? Decimal.zero(),
          quantityReturnedV2: Decimal.zero(),
          returnedFromSaleProductId: undefined,
          returnedFromSaleId: undefined,
        }
        state.saleProducts[productIdx] = applySaleUomSelection({
          saleProduct: state.saleProducts[productIdx],
          usage: "sale",
          uomId: state.saleProducts[productIdx].uomId,
        })
        state.isEdited = checkIsEdited(state as SaleFormState)
        return
      }

      if (isReturn(state.saleProducts[productIdx])) {
        state.saleProducts[productIdx] = {
          ...state.saleProducts[productIdx],
          quantityReturnedV2: action.payload.quantity
            ? action.payload.quantity.mult(Decimal.fromBigSource(-1))
            : Decimal.zero(),
        }
        state.saleProducts[productIdx] = applySaleUomSelection({
          saleProduct: state.saleProducts[productIdx],
          usage: "return",
          uomId: state.saleProducts[productIdx].uomIdReturned,
        })
        return
      } else {
        const newQty = action.payload.quantity
          ? action.payload.quantity
          : Decimal.zero()
        state.saleProducts[productIdx] = {
          ...state.saleProducts[productIdx],
          quantityV2: newQty,
        }
        state.saleProducts[productIdx] = applySaleUomSelection({
          saleProduct: state.saleProducts[productIdx],
          usage: "sale",
          uomId: state.saleProducts[productIdx].uomId,
        })
      }

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setSaleProductUom: (
      state,
      action: PayloadAction<{
        id: PBUUID
        usage: SaleUomUsage
        uomId?: PBUUID
        uomSnapshot?: LedgerEntry_UomSnapshot
      }>
    ) => {
      const { id, usage, uomId, uomSnapshot } = action.payload
      if (!id || !state.saleProducts) {
        return
      }

      const productIdx = findIdxInSaleProducts(state.saleProducts, id)
      if (productIdx === -1) {
        return
      }

      // Preserve the displayed quantity across UOM swaps;
      const sp = state.saleProducts[productIdx]
      const oldInfo = getSaleUomInfo({ saleProduct: sp, usage })
      const oldBase = Decimal.fromPB(
        (usage === "return" ? sp.quantityReturnedV2 : sp.quantityV2) ??
          Decimal.zero()
      )
      const display = safeConvertQuantityToUoMQuantity(
        oldBase,
        oldInfo.uomId ? oldInfo.conversionFactorToBase : undefined
      )
      const newFactor =
        uomId && uomSnapshot?.conversionFactorToBase
          ? Decimal.fromPB(uomSnapshot.conversionFactorToBase)
          : Decimal.fromBigSource(1)
      const newBase = display.mult(newFactor)

      const updated: SaleProduct = {
        ...state.saleProducts[productIdx],
        ...(usage === "return"
          ? {
              uomIdReturned: uomId,
              uomSnapshotReturned: uomSnapshot,
              quantityReturnedV2: newBase,
            }
          : {
              uomId,
              uomSnapshot,
              quantityV2: newBase,
            }),
      }

      state.saleProducts[productIdx] = applySaleUomSelection({
        saleProduct: updated,
        usage,
        uomId,
      })

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setProductPriceV2: (
      state,
      action: ProductPayloadAction<{
        price: FractionalMoney
      }>
    ) => {
      if (!action.payload.id || !state.saleProducts) {
        return
      }

      const productIdx = findIdxInSaleProducts(
        state.saleProducts,
        action.payload.id
      )

      if (productIdx === -1) {
        return
      }

      state.saleProducts[productIdx] = {
        ...state.saleProducts[productIdx],
        price: action.payload.price.toMoney(),
        priceFractional: action.payload.price,
        isPriceEdited: true,
      }

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setProductIsTaxed: (
      state,
      action: ProductPayloadAction<{
        isTaxed: boolean
      }>
    ) => {
      if (!state.saleProducts) {
        return
      }
      if (!action.payload.id) {
        return
      }
      const productIdx = findIdxInSaleProducts(
        state.saleProducts,
        action.payload.id
      )
      if (productIdx === -1) {
        return
      }
      state.saleProducts[productIdx] = {
        ...state.saleProducts[productIdx],
        isTaxed: action.payload.isTaxed,
        isTaxedEdited: true,
      }
      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    toggleSaleProductAdditionalFee: (
      state,
      action: ProductPayloadAction<{
        feeId: string
        disabled: boolean
      }>
    ) => {
      if (!action.payload.id || !state.saleProducts) {
        return
      }

      const productIdx = findIdxInSaleProducts(
        state.saleProducts,
        action.payload.id
      )

      if (productIdx === -1) {
        return
      }

      const sp = state.saleProducts[productIdx]
      state.saleProducts[productIdx] = {
        ...sp,
        additionalFees: sp.additionalFees.map((fee) =>
          fee.feeId === action.payload.feeId
            ? { ...fee, isDisabled: action.payload.disabled }
            : fee
        ),
      }

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setGiftCardDetails: (
      state,
      action: ProductPayloadAction<{
        giftCardId: PBUUID
        initialBalance: Money
        price: Money
      }>
    ) => {
      if (!action.payload.id || !state.saleProducts) {
        return
      }

      const productIdx = findIdxInSaleProducts(
        state.saleProducts,
        action.payload.id
      )

      if (productIdx === -1) {
        return
      }

      state.saleProducts[productIdx] = {
        ...state.saleProducts[productIdx],
        giftCardId: action.payload.giftCardId,
        price: action.payload.price,
        tier1Price: action.payload.price,
        priceFractional: FractionalMoney.fromMoney(action.payload.price),
        giftCardInitialBalance: action.payload.initialBalance,
        isPriceEdited: true,
      }

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setProductCostV2: (
      state,
      action: ProductPayloadAction<{
        cost: FractionalMoney
      }>
    ) => {
      if (!action.payload.id || !state.saleProducts) {
        return
      }

      const productIdx = findIdxInSaleProducts(
        state.saleProducts,
        action.payload.id
      )

      if (productIdx === -1) {
        return
      }

      state.saleProducts[productIdx] = {
        ...state.saleProducts[productIdx],
        costV2: action.payload.cost,
        isCostEdited: true,
      }

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setAddress: (state, action: PayloadAction<PBAddress | undefined>) => {
      state.address = action.payload

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setPurchaseOrder: (
      state,
      action: PayloadAction<PurchaseOrder | undefined>
    ) => {
      state.purchaseOrder = action.payload

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setCustomerContact: (
      state,
      action: PayloadAction<CustomerContact | undefined>
    ) => {
      state.customerContact = action.payload

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setSaleNotes: (state, action: PayloadAction<string>) => {
      state.saleNotes = action.payload

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setInternalSaleNotes: (state, action: PayloadAction<string>) => {
      state.internalSaleNotes = action.payload

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setDesignatedClerk: (state, action: PayloadAction<Staff | null>) => {
      state.designatedClerkId = action.payload?.id ?? null
      state.isEdited = checkIsEdited(state)
    },
    setSpecialOrderPriceAndCost: (
      state,
      action: ProductPayloadAction<{ data: SpecialOrderProductSaleFormData }>
    ) => {
      const { id, data } = action.payload

      if (!id || !state.saleProducts) {
        return
      }

      const productIdx = findIdxInSaleProducts(state.saleProducts, id)

      if (productIdx === -1) {
        return
      }

      state.saleProducts[productIdx] = {
        ...state.saleProducts[productIdx],
        price: data.price,
        tier1Price: data.retailPrice,
        priceFractional: FractionalMoney.fromMoney(data.price),
        costV2: data.cost,
        isPriceEdited: true,
        isCostEdited: true,
      }

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    searchProductSelected: (
      state,
      action: PayloadAction<
        {
          productResult: TransactionProduct
          saleProductId?: PBUUID
          productHasSaleForm?: boolean
        } & ItemSetSubmissionArgs
      >
    ) => {
      state.searchProduct = {
        ...action.payload.productResult,
      }

      if (
        action.payload.productResult.isScan &&
        action.payload.productResult.quantity.gt(Decimal.zero())
      ) {
        state.addProductQuantity = action.payload.productResult.quantity
      }

      if (
        !state.addProductQuantity?.isNegative() &&
        !action.payload.productHasSaleForm &&
        action.payload.productResult?.isScan &&
        !action.payload.productResult.product?.isTintable &&
        !action.payload.productResult.product?.isSpecialOrderableV2 &&
        !action.payload.productResult.product?.isNonStock
      ) {
        const itemSetArgs: ItemSetSubmissionArgs = {
          assignToItemSet: action.payload.assignToItemSet,
          itemSetId: action.payload.itemSetId,
          itemSetIdBySaleProductId: action.payload.itemSetIdBySaleProductId,
        }
        salesSlice.caseReducers.searchProductSubmitted(state, {
          type: "searchProductSubmitted",
          payload: {
            saleProductId: action.payload.saleProductId,
            uomId: action.payload.productResult.uomId,
            uomSnapshot: action.payload.productResult.uomSnapshot,
            ...itemSetArgs,
          },
        })
      }
    },
    searchProductSubmitted: (
      state,
      action: PayloadAction<SearchProductSubmittedPayload>
    ) => {
      const searchProduct = state.searchProduct?.product
      const {
        uomId: submittedUomId,
        uomSnapshot: submittedUomSnapshot,
        notes = "",
        internalNotes = "",
        tintColor,
        customTintColor = "",
        quantity,
        saleProductType = SaleProductType.SALE,
        linkedTransactionProductId,
        linkedTransactionId,
        linkedTransactionIdentifier = "",
        linkedTransactionType = SaleType.SALE,
        linkedQuantityRemaining = Decimal.zero(),
        saleProductId,
        giftCardId,
        giftCardInitialBalance,
        giftCardPrice,
        customFields,
        itemSetId,
        itemSetIdBySaleProductId,
        assignToItemSet,
      } = action.payload

      if (!searchProduct?.id || (customTintColor && tintColor)) {
        return
      }

      if (!state.productsById) {
        state.productsById = {}
      }

      if (!state.inventoriesByProductId) {
        state.inventoriesByProductId = {}
      }

      if (!state.catalogProductsById) {
        state.catalogProductsById = {}
      }

      const alwaysAddNew =
        searchProduct.isSpecialOrderableV2 ||
        searchProduct.isNonStock ||
        saleProductType === SaleProductType.RETURN ||
        saleProductType === SaleProductType.UNLINKED_RETURN ||
        searchProduct.shouldNotNest ||
        searchProduct.saleFormId
      const searchProductIDString = UUID.fromPB(searchProduct.id).toString()

      if (!state.saleProducts) {
        state.saleProducts = []
      }

      const idx = getExistingProductWithNotesInCartIdx(
        searchProduct.id,
        state.saleProducts,
        {
          receiptNotes: notes,
          internalNotes,
          tintColor,
          customTintColor,
          customFields: customFields ?? [],
        },
        saleProductType,
        itemSetId,
        itemSetIdBySaleProductId
      )
      const lastPositionV2 = state.saleProducts.length

      const submittedQuantity = giftCardId
        ? Decimal.fromBigSource(1)
        : quantity ?? state.addProductQuantity ?? Decimal.zero()

      const hasIdx = idx != undefined && idx !== -1
      const existingSaleProduct = hasIdx ? state.saleProducts[idx] : undefined

      const usage: SaleUomUsage = isReturnType(saleProductType)
        ? "return"
        : "sale"

      const existingUomId =
        usage === "return"
          ? existingSaleProduct?.uomIdReturned
          : existingSaleProduct?.uomId
      const existingUomSnapshot =
        usage === "return"
          ? existingSaleProduct?.uomSnapshotReturned
          : existingSaleProduct?.uomSnapshot

      // If the selected/search UOM differs from the existing cart item's UOM,
      // do not nest/merge quantities; add a separate line item.
      const shouldUpdateExisting =
        hasIdx && !alwaysAddNew && UUID.eqFromPB(existingUomId, submittedUomId)

      const { selectedUomId, selectedUomSnapshot } = shouldUpdateExisting
        ? {
            selectedUomId: existingUomId,
            selectedUomSnapshot: existingUomSnapshot,
          }
        : {
            selectedUomId: submittedUomId,
            selectedUomSnapshot: submittedUomSnapshot,
          }

      // Interpret the entered quantity as the selected UOM quantity when a UOM
      // is selected (or defaulted), and normalize to base units.
      const baseSubmittedQuantity =
        selectedUomId && selectedUomSnapshot?.conversionFactorToBase
          ? convertUoMQuantityToQuantity(
              submittedQuantity,
              Decimal.fromPB(selectedUomSnapshot.conversionFactorToBase)
            )
          : submittedQuantity

      if (shouldUpdateExisting) {
        // Updating existing sale product
        const updatedProduct = {
          ...state.saleProducts[idx],
          notes: notes ?? state.saleProducts[idx].notes,
          internalNotes: internalNotes ?? state.saleProducts[idx].internalNotes,
          isActive: true,
        }
        const quantityV2 = state.saleProducts[idx].quantityV2
          ? Decimal.fromPB(state.saleProducts[idx].quantityV2)
          : Decimal.zero()
        updatedProduct.quantityV2 = quantityV2.add(baseSubmittedQuantity)
        updatedProduct.tintColorId = tintColor?.id
        updatedProduct.customTintColor = customTintColor

        state.saleProducts[idx] = applySaleUomSelection({
          saleProduct: updatedProduct,
          usage,
          uomId: selectedUomId,
        })
        const prevVersion = state.lastModifiedSaleProduct?.version ?? 0
        state.lastModifiedSaleProduct = {
          id: updatedProduct.id!,
          version: prevVersion + 1,
        }
      } else {
        const isSale = saleProductType === SaleProductType.SALE
        const isSpecialOrder =
          !!state.searchProduct?.product?.isSpecialOrderableV2
        const isNonStock = !!state.searchProduct?.product?.isNonStock
        const canEditPriceCost = isSale && (isSpecialOrder || isNonStock)
        const price =
          canEditPriceCost && action.payload.isPriceEdited
            ? action.payload.customPrice
            : undefined
        const retailPrice =
          canEditPriceCost && action.payload.isPriceEdited && isSpecialOrder
            ? action.payload.customRetailPrice
            : undefined
        const cost =
          canEditPriceCost && action.payload.isCostEdited
            ? action.payload.customCost
            : undefined

        // Adding new sale product
        const newSaleProduct = newSaleProductV2FromProduct({
          addProductQuantity: baseSubmittedQuantity,
          customTintColor,
          notes: notes || searchProduct?.templateSaleProductNotes || "",
          internalNotes: internalNotes || "",
          position: lastPositionV2 + 1,
          product: state.searchProduct?.product,
          additionalFees: state.searchProduct?.additionalFees,
          tintColor,
          tier1Price: retailPrice,
          price: price,
          cost: cost,
          customFields: customFields,
          linkedTransactionProductId: linkedTransactionProductId,
          linkedTransactionId: linkedTransactionId,
          saleProductType,
          id: saleProductId,
        })
        const newWithSelection: SaleProduct = {
          ...newSaleProduct,
          ...(usage === "return"
            ? {
                uomIdReturned: selectedUomId,
                uomSnapshotReturned: selectedUomSnapshot,
              }
            : { uomId: selectedUomId, uomSnapshot: selectedUomSnapshot }),
          ...(giftCardId && { giftCardId }),
          ...(giftCardInitialBalance && {
            price: giftCardPrice,
            tier1Price: giftCardPrice,
            giftCardInitialBalance: giftCardInitialBalance,
            isPriceEdited: true,
          }),
        }
        const newWithUom = applySaleUomSelection({
          saleProduct: newWithSelection,
          usage,
          uomId: selectedUomId,
        })
        state.saleProducts = [...(state.saleProducts ?? []), newWithUom]
        const prevVersion = state.lastModifiedSaleProduct?.version ?? 0
        state.lastModifiedSaleProduct = {
          id: newSaleProduct.id!,
          version: prevVersion + 1,
          isNewLine: true,
        }

        if (newSaleProduct.id && !isReturnType(saleProductType)) {
          assignToItemSet(UUID.fromPB(newSaleProduct.id))
        }
      }

      if (linkedTransactionId) {
        const saleIdString = UUID.idString(linkedTransactionId)
        if (!state.returnedFromSalesById) {
          state.returnedFromSalesById = {}
        }
        state.returnedFromSalesById[saleIdString] = {
          id: linkedTransactionId,
          type: linkedTransactionType,
          identifier: linkedTransactionIdentifier,
        }
      }

      if (linkedTransactionProductId) {
        if (!state.quantityRemainingBySaleProductId) {
          state.quantityRemainingBySaleProductId = {}
        }
        state.quantityRemainingBySaleProductId[
          linkedTransactionProductId.toString()
        ] = linkedQuantityRemaining
      }

      state.productsById = {
        ...state.productsById,
        [searchProductIDString]: searchProduct,
      }

      if (state.searchProduct?.inventories) {
        state.inventoriesByProductId = {
          ...state.inventoriesByProductId,
          [searchProductIDString]: state.searchProduct.inventories,
        }
      }

      if (state.searchProduct?.catalogProduct) {
        state.catalogProductsById = {
          ...state.catalogProductsById,
          [searchProductIDString]: state.searchProduct.catalogProduct,
        }
      }

      if (tintColor?.id) {
        state.tintColorsById = {
          ...state.tintColorsById,
          [UUID.fromPB(tintColor.id).toString()]: tintColor,
        }
      }
      state.addProductQuantity = Decimal.fromBigSource(1)
      delete state.searchProduct

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    addColorxTintedProductsToCartV2: (
      state,
      action: PayloadAction<
        {
          colorxProducts: ColorxTintedProductsResult[]
        } & ItemSetSubmissionArgs
      >
    ) => {
      if (!action?.payload?.colorxProducts?.length) {
        return
      }

      if (!state.saleProducts) {
        state.saleProducts = []
      }

      if (!state.productsById) {
        state.productsById = {}
      }

      if (!state.inventoriesByProductId) {
        state.inventoriesByProductId = {}
      }

      if (!state.catalogProductsById) {
        state.catalogProductsById = {}
      }

      for (const colorxProduct of action.payload.colorxProducts) {
        const {
          colorxIdentifier,
          product,
          catalogProduct,
          tintColor,
          customTintColor,
          notes: formula,
          additionalFees,
        } = colorxProduct
        if (!product || !product.id) {
          continue
        }
        let notes = colorxIdentifier
        if (formula) {
          notes += `: ${formula}`
        }

        const lastPosition: number = state.saleProducts.length ?? 0
        const newSaleProduct = newSaleProductV2FromProduct({
          addProductQuantity: colorxProduct.quantityV2
            ? Decimal.fromPB(colorxProduct.quantityV2)
            : Decimal.zero(),
          customTintColor,
          notes,
          position: lastPosition + 1,
          product,
          additionalFees,
          tintColor,
          colorxTintedProductId: colorxProduct.colorxTintedProductId,
          saleProductType: SaleProductType.SALE,
        })
        state.saleProducts = [...(state.saleProducts ?? []), newSaleProduct]

        if (newSaleProduct.id) {
          action.payload.assignToItemSet(UUID.fromPB(newSaleProduct.id))
          const prevVersion = state.lastModifiedSaleProduct?.version ?? 0
          state.lastModifiedSaleProduct = {
            id: newSaleProduct.id,
            version: prevVersion + 1,
            isNewLine: true,
          }
        }

        state.productsById = {
          ...state.productsById,
          [UUID.fromPB(product.id).toString()]: product,
        }

        if (colorxProduct?.inventories && colorxProduct?.product?.id) {
          state.inventoriesByProductId = {
            ...state.inventoriesByProductId,
            [UUID.fromPB(colorxProduct.product.id).toString()]:
              colorxProduct.inventories,
          }
        }

        if (tintColor?.id) {
          state.tintColorsById = {
            ...state.tintColorsById,
            [UUID.fromPB(tintColor.id).toString()]: tintColor,
          }
        }

        if (catalogProduct) {
          state.catalogProductsById = {
            ...state.catalogProductsById,
            [UUID.fromPB(product.id).toString()]: catalogProduct,
          }
        }
      }

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setProductNote: (
      state,
      action: PayloadAction<{
        productToEditId: PBUUID | undefined
        field: "notes" | "internalNotes"
        text: string
      }>
    ) => {
      const { productToEditId, field, text } = action.payload
      if (!productToEditId || !state.saleProducts) {
        return
      }

      const productIdx = findIdxInSaleProducts(
        state.saleProducts,
        productToEditId
      )

      if (productIdx === -1) {
        return
      }

      state.saleProducts[productIdx] = {
        ...state.saleProducts[productIdx],
        [field]: text,
      }

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setProductTint: (
      state,
      action: PayloadAction<
        { productToEditId: PBUUID | undefined } & ProductTintSelection
      >
    ) => {
      const { productToEditId, ...tint } = action.payload
      updateProductTint(state, productToEditId, tint)
    },
    setProductTintCustomFieldsAndNotes: (
      state,
      action: PayloadAction<
        {
          productToEditId: PBUUID | undefined
          customFields: SaleForm_CustomField[]
          notes: string
          internalNotes: string
        } & ProductTintSelection
      >
    ) => {
      const {
        productToEditId,
        tintColor,
        customTintColor,
        customFields,
        notes,
        internalNotes,
      } = action.payload
      const tint: ProductTintSelection = tintColor
        ? { tintColor, customTintColor: "" }
        : { tintColor: undefined, customTintColor }
      updateProductTint(state, productToEditId, tint, {
        notes,
        internalNotes,
        customFields,
      })
    },
    setTenderOption(state, action: PayloadAction<OptionType>) {
      state.tenderOption = action.payload
    },
    resetTenderOption(state) {
      state.tenderOption = undefined
    },
    resetCartTotals: (state) => {
      delete state.cartTotals

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    resetSearchProductSelected: (state) => {
      delete state.searchProduct

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    clearLastModifiedSaleProduct: (state) => {
      state.lastModifiedSaleProduct = undefined
    },
    setSplitAmount(state, action: PayloadAction<Money>) {
      state.splitAmount = action.payload
    },
    setIsCardSurchargeExempt(state, action: PayloadAction<boolean>) {
      state.isCardSurchargeExempt = action.payload
    },
    addSplitPayment: (
      state,
      action: PayloadAction<DisplayableTenderSubmissionMetadata>
    ) => {
      const tsm = action.payload
      if (
        tsm.submissionType === TenderSubmissionMetadata_SubmissionType.UNDEFINED
      ) {
        return
      }

      if (!tsm.amount || Money.fromPB(tsm.amount).lt(Money.zero())) {
        return
      }

      const updatedPayments = [...(state.splitPayments ?? []), tsm]

      state.splitPayments = updatedPayments
      state.splitAmount = undefined
    },
    toggleIsSplitPayment(state) {
      state.isSplitPayment = !state.isSplitPayment

      state.splitAmount = undefined
      state.splitPayments = []
    },
    resetPayments(state) {
      state.isSplitPayment = false
      state.tenderOption = undefined
      state.splitAmount = undefined
      state.splitPayments = []
    },
    setTaxRateId(state, action: PayloadAction<PBUUID>) {
      state.taxRateIds = sortTaxRateIds([action.payload])
      state.isTaxRateFromDeliveryAddress = false

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setTaxRateIds(state, action: PayloadAction<PBUUID[]>) {
      state.taxRateIds = sortTaxRateIds(action.payload)
      state.isTaxRateFromDeliveryAddress = false

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    setDeliveryAddressTaxRateId(state, action: PayloadAction<PBUUID>) {
      state.taxRateIds = sortTaxRateIds([action.payload])
      state.isTaxRateFromDeliveryAddress = true

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    clearDeliveryAddressTaxRate(state) {
      if (!state.isTaxRateFromDeliveryAddress) return
      state.taxRateIds = []
      state.isTaxRateFromDeliveryAddress = false

      state.isEdited = checkIsEdited(state as SaleFormState)
    },
    customerLoaded(state, action: PayloadAction<Customer | undefined>) {
      state.defaultTaxRateId = action.payload?.taxRateId
    },

    setUnlinkedReturnDetails: (
      state,
      action: PayloadAction<{
        saleProductId: PBUUID | undefined
        signedQuantity: Decimal
        notes: string
        internalNotes: string
        uomIdReturned?: PBUUID
        uomSnapshotReturned?: LedgerEntry_UomSnapshot
      }>
    ) => {
      const {
        saleProductId,
        signedQuantity,
        notes,
        internalNotes,
        uomIdReturned,
        uomSnapshotReturned,
      } = action.payload

      if (!action.payload.saleProductId) return

      const spIdx = (state.saleProducts ?? []).findIndex((sp) =>
        UUID.eqFromPB(sp.id, saleProductId)
      )
      if (spIdx === -1 || !state.saleProducts) return

      const sp = state.saleProducts[spIdx]
      const updated: SaleProduct = {
        ...sp,
        type: SaleProductType.UNLINKED_RETURN,
        quantityReturnedV2: signedQuantity.mult(Decimal.fromBigSource(-1)),
        quantityV2: Decimal.zero(),
        notes,
        internalNotes,
        returnedFromSaleProductId: undefined,
        returnedFromSaleId: undefined,
        ...(uomIdReturned ? { uomIdReturned } : {}),
        ...(uomSnapshotReturned ? { uomSnapshotReturned } : {}),
      }

      state.saleProducts[spIdx] = applySaleUomSelection({
        saleProduct: updated,
        usage: "return",
        uomId: updated.uomIdReturned,
      })
    },
    setReturnDetails: (
      state,
      action: PayloadAction<{
        saleProductId?: PBUUID
        returnedFromSaleProductId?: PBUUID
        returnedFromSaleLink: SaleLink
        signedQuantity: Decimal
        notes: string
        internalNotes: string
        returnedFromQuantityRemaining: Decimal
        uomIdReturned?: PBUUID
        uomSnapshotReturned?: LedgerEntry_UomSnapshot
      }>
    ) => {
      const {
        saleProductId,
        returnedFromSaleProductId,
        returnedFromSaleLink,
        signedQuantity,
        notes = "",
        internalNotes = "",
        returnedFromQuantityRemaining,
        uomIdReturned,
        uomSnapshotReturned,
      } = action.payload
      if (!saleProductId) return

      const spIdx = (state.saleProducts ?? []).findIndex((sp) =>
        UUID.eqFromPB(sp.id, saleProductId)
      )
      if (spIdx === -1 || !state.saleProducts) return

      const sp = state.saleProducts[spIdx]
      const updated: SaleProduct = {
        ...sp,
        type: SaleProductType.RETURN,
        returnedFromSaleProductId: returnedFromSaleProductId,
        returnedFromSaleId: returnedFromSaleLink.id,
        quantityReturnedV2: signedQuantity.mult(Decimal.fromBigSource(-1)),
        quantityV2: Decimal.zero(),
        notes,
        internalNotes,
        uomIdReturned,
        uomSnapshotReturned,
      }
      state.saleProducts[spIdx] = applySaleUomSelection({
        saleProduct: updated,
        usage: "return",
        uomId: updated.uomIdReturned,
      })

      if (!state.returnedFromSalesById) {
        state.returnedFromSalesById = {}
      }

      const saleIdString = UUID.idString(returnedFromSaleLink.id)
      if (!state.returnedFromSalesById[saleIdString]) {
        const link: SaleLink = {
          id: returnedFromSaleLink.id,
          type: returnedFromSaleLink.type,
          identifier: returnedFromSaleLink.identifier,
        }
        state.returnedFromSalesById[saleIdString] = link
      }

      if (!state.quantityRemainingBySaleProductId) {
        state.quantityRemainingBySaleProductId = {}
      }
      state.quantityRemainingBySaleProductId[
        UUID.idString(returnedFromSaleProductId)
      ] = returnedFromQuantityRemaining
    },
    setRestockFee: (state, action: PayloadAction<RestockFee>) => {
      state.restockFee = action.payload
    },
    setFulfillmentLocation: (
      state,
      action: PayloadAction<StrictFulfillment>
    ) => {
      state.fulfillment = action.payload
      state.isEdited = checkIsEdited(state)
    },
    setDiscount: (state, action: PayloadAction<Discount>) => {
      state.discount = action.payload
      state.isEdited = checkIsEdited(state)
    },
    clearDiscount: (state) => {
      state.discount = null
      state.isEdited = checkIsEdited(state)
    },
    updateTintColorsById: (
      state,
      action: PayloadAction<TintColor | undefined>
    ) => {
      const tintColor = action.payload
      if (!tintColor?.id) {
        return
      }
      state.tintColorsById = {
        ...state.tintColorsById,
        [UUID.fromPB(tintColor.id).toString()]: tintColor,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      salesApiSlice.endpoints.syncCartPrices.matchFulfilled,
      (state, { payload }) => {
        state.cartTotals = payload
        if (payload.initialDeposit && !state.chosenDepositValue) {
          // Seed the intent from the first SCP response, which revives a stored
          // deposit or falls back to the company default.
          const depositBasis = Money.fromPB(payload.depositBasis)
          if (depositBasis.isPositive()) {
            const initialDeposit = Money.fromPB(payload.initialDeposit)
            state.chosenDepositValue = {
              type: ValueTypes.PERCENTAGE,
              percentage: Decimal.fromBigSource(
                initialDeposit.div(depositBasis)
              ).mult(Decimal.fromBigSource(100)),
            }
          }
        }
        // Lines the pricing engine added (e.g. PPC's surcharge) join the cart
        // directly, leaving any in-progress product search untouched; the next
        // sync prices them as normal lines.
        for (const { saleProduct, product } of payload.addedProducts) {
          const productId = product?.id
          if (
            !saleProduct ||
            !product ||
            !productId ||
            state.saleProducts?.some((sp) =>
              UUID.eqFromPB(sp.productId, productId)
            )
          ) {
            continue
          }
          const saleProducts = state.saleProducts ?? []
          state.saleProducts = [
            ...saleProducts,
            { ...saleProduct, position: saleProducts.length + 1 },
          ]
          state.productsById = {
            ...state.productsById,
            [UUID.fromPB(productId).toString()]: product,
          }
          state.isEdited = checkIsEdited(state as SaleFormState)
        }
      }
    )
  },
})

export const {
  setIsInitializing,
  setSaleInitialState,
  resetSaleStateForEditableSale,
  resetSaleDetails,
  resetSaleForm,
  setQuoteExpiration,
  setInitialQuoteExpiration,
  customerSelected,
  fanBuilderCouponsChanged,
  resetCustomer,
  removeProduct,
  setAddProductQuantity,
  setProductQuantityV2,
  setSaleProductUom,
  setAddress,
  setPurchaseOrder,
  setCustomerContact,
  setSaleNotes,
  setInternalSaleNotes,
  setDesignatedClerk,
  searchProductSubmitted,
  searchProductSelected,
  resetSearchProductSelected,
  clearLastModifiedSaleProduct,
  setProductNote,
  setProductTint,
  setProductTintCustomFieldsAndNotes,
  setProductPriceV2,
  setProductIsTaxed,
  setGiftCardDetails,
  setProductCostV2,
  setTenderOption,
  resetCartTotals,
  incrementReturnQuantities,
  setSpecialOrderPriceAndCost,
  setIsCardSurchargeExempt,
  toggleSaleProductAdditionalFee,
  addSplitPayment,
  setSplitAmount,
  toggleIsSplitPayment,
  resetPayments,
  resetTenderOption,
  addColorxTintedProductsToCartV2,
  setTaxRateId,
  setTaxRateIds,
  setDeliveryAddressTaxRateId,
  clearDeliveryAddressTaxRate,
  customerLoaded,
  setReturnDetails,
  setUnlinkedReturnDetails,
  setRestockFee,
  setFulfillmentLocation,
  setAdditionalDeposit,
  setChosenDepositValue,
  setDiscount,
  clearDiscount,
  updateTintColorsById,
} = salesSlice.actions

export const selectIsEdited = (state: AppState) => !!state.sales.isEdited
export const selectIsInitializing = (state: AppState) =>
  state.sales.isInitializing

export const selectCustomerId = (state: AppState) => state.sales.customerId
export const selectFanBuilderCoupons = (state: AppState) =>
  state.sales.fanBuilderCoupons ?? emptyFanBuilderCoupons

// A stable empty array: a fresh [] on every read would re-run the cart price sync forever.
const emptyFanBuilderCoupons: ValidatedFanBuilderLoyaltyCoupon[] = []
export const selectPurchaseOrder = (state: AppState) =>
  state.sales.purchaseOrder
export const selectAddress = (state: AppState) => state.sales.address
export const selectCustomerContact = (state: AppState) =>
  state.sales.customerContact
export const selectSaleNotes = (state: AppState) => state.sales.saleNotes
export const selectInternalSaleNotes = (state: AppState) =>
  state.sales.internalSaleNotes
const EMPTY_SALE_PRODUCTS: SaleProduct[] = []
export const selectSaleProducts = (state: AppState) =>
  state.sales.saleProducts ?? EMPTY_SALE_PRODUCTS
export const selectChosenDepositValue = (state: AppState) =>
  state.sales.chosenDepositValue
export const selectHasSaleProducts = (state: AppState) =>
  !!state.sales.saleProducts && state.sales.saleProducts.length > 0
export const selectHasSpecialOrderProduct = (state: AppState) =>
  !!state.sales.saleProducts?.some(
    (sp) => sp.type == SaleProductType.SALE && sp.isSpecialOrderableV2
  )
export const selectHasDiscountableProducts = (state: AppState) =>
  state.sales.saleProducts?.some(
    (sp) =>
      !(sp.type == SaleProductType.SALE && sp.isSpecialOrderableV2) &&
      sp.type !== SaleProductType.UNLINKED_RETURN
  ) ?? false
export const selectQuoteExpiration = (state: AppState) =>
  state.sales.quoteExpiration
export const selectLocation = (state: AppState) => state.sales.location
export const selectFulfillment = (state: AppState) => state.sales.fulfillment
export const selectDesignatedClerkId = (state: AppState) =>
  state.sales.designatedClerkId
export const selectAdditionalDeposit = (state: AppState) =>
  state.sales.additionalDeposit
export const selectProductsById = (state: AppState) => state.sales.productsById
export const selectCatalogProductsById = (state: AppState) =>
  state.sales.catalogProductsById
export const selectTintColorsById = (state: AppState) =>
  state.sales.tintColorsById
export const selectCartTotals = (state: AppState) => state.sales.cartTotals
const EMPTY_SPLIT_PAYMENTS: DisplayableTenderSubmissionMetadata[] = []
export const selectSplitPayments = (state: AppState) =>
  state.sales.splitPayments ?? EMPTY_SPLIT_PAYMENTS
export const selectIsSplitPayment = (state: AppState) =>
  state.sales.isSplitPayment
export const selectSplitAmount = (state: AppState) => state.sales.splitAmount
export const selectSearchProduct = (state: AppState) =>
  state.sales.searchProduct
export const selectAddProductQuantity = (state: AppState) =>
  state.sales.addProductQuantity
export const selectSaleInventoriesByProductId = (state: AppState) =>
  state.sales.inventoriesByProductId
export const selectTaxRateId = (state: AppState) =>
  state.sales.taxRateIds?.[0] || state.sales.defaultTaxRateId

export const selectTaxRateIds = createSelector(
  (state: AppState) => state.sales.taxRateIds,
  (state: AppState) => state.sales.defaultTaxRateId,
  (taxRateIds, defaultTaxRateId): PBUUID[] => {
    if (taxRateIds?.length) return taxRateIds
    if (defaultTaxRateId) return [defaultTaxRateId]
    return []
  }
)
export const selectCartTaxRateId = createSelector(
  (state: AppState) => state.sales.cartTotals,
  (cartTotals) => UUID.fromPBOrUndefined(cartTotals?.taxRate?.id)
)
export const selectCartTaxRate = createSelector(
  (state: AppState) => state.sales.cartTotals,
  (cartTotals) =>
    cartTotals?.taxRate?.rate
      ? Decimal.fromPB(cartTotals.taxRate.rate)
      : Decimal.zero()
)
export const selectCartTaxRatesIds = createSelector(
  (state: AppState) => state.sales.cartTotals,
  (cartTotals) =>
    cartTotals?.saleTaxes?.baseTaxes
      ?.map((tax) => tax.taxRateId)
      .filter(isID) ?? []
)
export const selectCartTaxRatesByRateId = createSelector(
  (state: AppState) => state.sales.cartTotals,
  (cartTotals) =>
    cartTotals?.saleTaxes?.baseTaxes?.reduce(
      (acc, tax) => {
        acc[UUID.idString(tax.taxRateId)] = Decimal.fromPBOrZero(tax.rate)
        return acc
      },
      {} as Record<string, Decimal>
    ) ?? {}
)
export const selectCardSurchargeRate = createSelector(
  (state: AppState) => state.sales.cartTotals?.cardSurchargeRate,
  (cardSurchargeRate) =>
    cardSurchargeRate ? Decimal.fromPB(cardSurchargeRate) : Decimal.zero()
)
export const selectIsCardSurchargeExempt = (state: AppState) =>
  state.sales.isCardSurchargeExempt
export const selectIsCardPayment = (state: AppState) =>
  state.sales.tenderOption === OptionTypes.CARD
export const selectIsCashPayment = (state: AppState) =>
  state.sales.tenderOption === OptionTypes.CASH
export const selectSplitTotalSurcharge = createSelector(
  (state: AppState) => state.sales.splitPayments,
  (splitPayments) =>
    calculateSurchargeFromTenderSubmissions(splitPayments ?? [])
)
export const selectCurrentAmount = (state: AppState) =>
  state.sales.isSplitPayment
    ? state.sales.splitAmount
    : state.sales.cartTotals?.totalDueToday
export const selectTotalDueToday = (state: AppState) =>
  state.sales.cartTotals?.totalDueToday
export const selectIdempotencyKey = (state: AppState) =>
  state.sales.idempotencyKey
export const selectHasLinkedReturnProductsInCart = (state: AppState) => {
  return (selectSaleProducts(state) ?? []).some(isLinkedReturn)
}
export const selectHasReturnProductsInCart = (state: AppState) => {
  const saleProductSlice = selectSaleProducts(state) ?? []
  return saleProductSlice.some(isReturn)
}
export const selectHasUnlinkedReturnProductsInCart = (state: AppState) => {
  return (selectSaleProducts(state) ?? []).some(isUnlinkedReturn)
}
const EMPTY_LINKED_SALES_BY_ID: Record<string, SaleLink> = {}
export const selectLinkedSalesById = (state: AppState) =>
  state.sales.returnedFromSalesById ?? EMPTY_LINKED_SALES_BY_ID
export const selectSaleInitialState = (state: AppState) =>
  state.sales.initialState
const EMPTY_QUANTITY_REMAINING_BY_SALE_PRODUCT_ID: Record<string, Decimal> = {}
export const selectQuantityRemainingBySaleProductId = (state: AppState) =>
  state.sales.quantityRemainingBySaleProductId ??
  EMPTY_QUANTITY_REMAINING_BY_SALE_PRODUCT_ID
export const selectRestockFee = (state: AppState) => state.sales.restockFee
export const selectLastModifiedSaleProduct = (state: AppState) =>
  state.sales.lastModifiedSaleProduct
export const selectDiscount = (state: AppState) => state.sales.discount

function newSaleProductV2FromProduct(args: {
  addProductQuantity: Decimal
  customTintColor: string
  notes: string
  internalNotes?: string
  position: number
  product?: Product
  additionalFees?: AdditionalFee[]
  tintColor?: TintColor
  colorxTintedProductId?: PBUUID
  tier1Price?: Money
  price?: Money
  cost?: FractionalMoney
  customFields?: SaleForm_CustomField[]
  saleProductType: SaleProductType
  linkedTransactionProductId?: PBUUID
  linkedTransactionId?: PBUUID
  id?: PBUUID
}): SaleProduct {
  const {
    addProductQuantity,
    customTintColor,
    notes,
    internalNotes,
    position,
    product,
    additionalFees,
    tintColor,
    colorxTintedProductId,
    tier1Price,
    price,
    cost,
    customFields,
    id,
  } = args
  const fees = additionalFees ?? []
  const sp: SaleProduct = SaleProductPB.create({
    id: id ?? UUID.newRandom(),
    isActive: true,
    isCostEdited: !!cost,

    productId: product?.id,
    additionalFees: fees.map((fee) => ({
      name: fee.name,
      feeId: fee.idString,
      unitFee: fee.fee
        ? FractionalMoney.fromMoney(Money.fromPB(fee.fee))
        : undefined,
      isTaxed: !!fee.isTaxable,
    })),
    customTintColor,
    tintColorId: tintColor?.id,
    notes,
    internalNotes,
    isPriceEdited: !!price,
    position,
    isTaxed: product?.taxStatus !== Product_TaxStatus.NON_TAXABLE,
    colorxTintedProductId,
    tier1Price,
    price,
    costV2: cost,
    isSpecialOrderableV2: product?.isSpecialOrderableV2,
    customFields,
    isNonStock: product?.isNonStock,
    type: args.saleProductType,
  })

  if (isReturnType(args.saleProductType)) {
    sp.quantityReturnedV2 = addProductQuantity.mult(Decimal.fromBigSource(-1))
    sp.returnedFromSaleProductId = args.linkedTransactionProductId
    sp.returnedFromSaleId = args.linkedTransactionId
  } else {
    sp.quantityV2 = addProductQuantity
  }

  return sp
}
